import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${getBackendUrl()}/api`;

const MyLeaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    leave_type: 'Full Day',
    from_date: '',
    to_date: '',
    reason: ''
  });
  const [scheduledClasses, setScheduledClasses] = useState([]);
  const [classRemarks, setClassRemarks] = useState({});
  const [checkingClasses, setCheckingClasses] = useState(false);
  const [classesChecked, setClassesChecked] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState({});
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [totalHours, setTotalHours] = useState(0);

  useEffect(() => {
    fetchLeaves();
  }, []);

  // Auto-check classes when dates change
  useEffect(() => {
    if (leaveForm.from_date && leaveForm.to_date && showApplyModal) {
      checkScheduledClasses();
    }
  }, [leaveForm.from_date, leaveForm.to_date]);

  const fetchLeaves = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login to view leaves');
        setLoading(false);
        return;
      }

      // Get user info to check role
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const response = await axios.get(`${API}/payroll/leave-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeaves(response.data || []);
      
      // Set user info for display
      if (user) {
        setEmployeeInfo(user);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching leaves:', err);
      setError('Failed to load leave requests. Please ensure you are logged in.');
    } finally {
      setLoading(false);
    }
  };

  const checkScheduledClasses = async () => {
    if (!leaveForm.from_date || !leaveForm.to_date) {
      return;
    }

    try {
      setCheckingClasses(true);
      setClassesChecked(false);
      const token = localStorage.getItem('token');
      
      // Get employee record first
      const employeesResponse = await axios.get(`${API}/payroll/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const employee = employeesResponse.data[0];
      if (!employee) {
        alert('Employee record not found. Please contact administrator.');
        setCheckingClasses(false);
        return;
      }

      setEmployeeInfo(employee);

      // Use new endpoint to get scheduled classes
      const response = await axios.get(
        `${API}/payroll/employees/${employee.id}/scheduled-classes`,
        {
          params: {
            from_date: leaveForm.from_date,
            to_date: leaveForm.to_date
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const { scheduled_classes, total_hours, is_visiting_faculty } = response.data;
      setScheduledClasses(scheduled_classes);
      setTotalHours(total_hours);
      
      // Initialize remarks and selection for each class
      const initialRemarks = {};
      const initialSelection = {};
      scheduled_classes.forEach(session => {
        initialRemarks[session.session_id] = '';
        // For visiting faculty, all classes selected by default; for regular staff, auto-select all
        initialSelection[session.session_id] = true;
      });
      setClassRemarks(initialRemarks);
      setSelectedClasses(initialSelection);
      setClassesChecked(true);

    } catch (err) {
      console.error('Error checking scheduled classes:', err);
      alert(`Error checking scheduled classes: ${err.response?.data?.detail || err.message}. Please try again or contact admin.`);
      setClassesChecked(false);
      setScheduledClasses([]);
    } finally {
      setCheckingClasses(false);
    }
  };

  // Calculate selected hours for visiting faculty
  const calculateSelectedHours = () => {
    let hours = 0;
    scheduledClasses.forEach(session => {
      if (selectedClasses[session.session_id]) {
        hours += session.duration_hours || 0;
      }
    });
    return hours.toFixed(2);
  };

  const handleApplyLeave = async () => {
    try {
      if (!leaveForm.from_date || !leaveForm.to_date || !leaveForm.reason) {
        alert('Please fill in all fields');
        return;
      }

      // DATE VALIDATION: Ensure To Date is not before From Date
      const fromDate = new Date(leaveForm.from_date);
      const toDate = new Date(leaveForm.to_date);
      
      if (toDate < fromDate) {
        alert('Invalid date range: "To Date" cannot be before "From Date". Please correct the dates.');
        return;
      }

      // MANDATORY: Must check for scheduled classes before submitting
      if (!classesChecked) {
        alert('Please click "Check Scheduled Classes" button to verify if you have any classes during this period.');
        return;
      }

      // For visiting faculty, check if classes are selected
      const isVisitingFaculty = employeeInfo?.employment_type?.toLowerCase().includes('visiting');
      
      // Check if there are scheduled classes and handle accordingly
      if (scheduledClasses.length > 0) {
        // Filter to get only selected classes
        const selectedSessionIds = Object.keys(selectedClasses).filter(id => selectedClasses[id]);
        const selectedSessions = scheduledClasses.filter(session => 
          selectedClasses[session.session_id]
        );

        // For visiting faculty, check if at least one class is selected
        if (isVisitingFaculty && selectedSessions.length === 0) {
          alert('Please select at least one class that will be affected by your leave.');
          return;
        }

        // Check for missing remarks on selected classes
        const missingRemarks = selectedSessions.filter(session => {
          const remark = classRemarks[session.session_id] || '';
          return remark.trim() === '';
        });

        if (missingRemarks.length > 0) {
          alert(`Please provide substitute/remarks for all ${selectedSessions.length} selected classes during your leave period.`);
          return;
        }
      }

      // Calculate days count (reuse fromDate and toDate from validation above)
      const timeDiff = toDate.getTime() - fromDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end date

      const token = localStorage.getItem('token');
      const employee = employeeInfo || (await axios.get(`${API}/payroll/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      })).data[0];
      
      if (!employee) {
        alert('Employee record not found. Please contact administrator.');
        return;
      }

      // Prepare affected classes data - only include selected classes
      const affectedClasses = scheduledClasses
        .filter(session => selectedClasses[session.session_id])
        .map(session => ({
          session_id: session.session_id,
          subject_name: session.subject_name || 'Subject',
          batch_name: session.batch_name || 'Batch',
          scheduled_date: session.scheduled_date,
          time_slot: session.time_slot || 'Time',
          classroom_name: session.classroom_name || 'Classroom',
          alternate_arrangement: classRemarks[session.session_id] || ''
        }));

      await axios.post(
        `${API}/payroll/leave-requests`,
        {
          employee_id: employee.id,
          leave_type: leaveForm.leave_type,
          from_date: leaveForm.from_date,
          to_date: leaveForm.to_date,
          days_count: daysDiff,
          reason: leaveForm.reason,
          affected_classes: affectedClasses
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Leave request submitted successfully!');
      setShowApplyModal(false);
      setLeaveForm({ leave_type: 'Full Day', from_date: '', to_date: '', reason: '' });
      setScheduledClasses([]);
      setClassRemarks({});
      setSelectedClasses({});
      setEmployeeInfo(null);
      fetchLeaves();
    } catch (err) {
      console.error('Error applying for leave:', err);
      const errorMessage = err.response?.data?.detail 
        ? (Array.isArray(err.response.data.detail) 
          ? err.response.data.detail.map(e => e.msg).join(', ')
          : err.response.data.detail)
        : 'Failed to apply for leave';
      alert(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-lg text-gray-600">Loading leave requests...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {employeeInfo?.role === 'super_admin' || employeeInfo?.role === 'admin' 
              ? 'All Leave Requests' 
              : 'My Leaves'}
          </h1>
          <p className="text-gray-600 mt-1">
            {employeeInfo?.role === 'super_admin' || employeeInfo?.role === 'admin'
              ? 'View and manage all employee leave requests'
              : 'View and manage your leave requests'}
          </p>
        </div>
        <button
          onClick={() => {
            setShowApplyModal(true);
            setClassesChecked(false);
            setScheduledClasses([]);
            setClassRemarks({});
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Apply for Leave
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {!error && leaves.length === 0 && (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-semibold mb-2">No Leave Requests</h3>
          <p className="text-gray-600 mb-4">You haven't applied for any leaves yet.</p>
          <button
            onClick={() => {
            setShowApplyModal(true);
            setClassesChecked(false);
            setScheduledClasses([]);
            setClassRemarks({});
          }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Apply for Leave
          </button>
        </div>
      )}

      {leaves.length > 0 && (
        <div className="space-y-6">
          {/* Group leaves by month based on from_date */}
          {(() => {
            // Group leaves by month
            const leavesByMonth = leaves.reduce((acc, leave) => {
              const leaveDate = new Date(leave.from_date);
              const monthYear = `${leaveDate.getFullYear()}-${String(leaveDate.getMonth() + 1).padStart(2, '0')}`;
              const monthName = leaveDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
              
              if (!acc[monthYear]) {
                acc[monthYear] = {
                  monthName,
                  leaves: [],
                  sortDate: leaveDate
                };
              }
              acc[monthYear].leaves.push(leave);
              return acc;
            }, {});
            
            // Sort months in descending order (most recent first)
            const sortedMonths = Object.entries(leavesByMonth).sort((a, b) => 
              b[1].sortDate - a[1].sortDate
            );
            
            return sortedMonths.map(([monthKey, monthData]) => (
              <div key={monthKey} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                {/* Month Header */}
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-300">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center">
                    <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {monthData.monthName}
                  </h2>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {monthData.leaves.length} {monthData.leaves.length === 1 ? 'Leave' : 'Leaves'}
                  </span>
                </div>
                
                {/* Leaves in this month */}
                <div className="space-y-3">
                  {monthData.leaves
                    .sort((a, b) => new Date(b.from_date) - new Date(a.from_date)) // Sort by date within month (recent first)
                    .map(leave => (
                      <div key={leave.id} className="bg-white shadow rounded-lg p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{leave.leave_type}</h3>
                            {/* Show employee name for admins */}
                            {(employeeInfo?.role === 'super_admin' || employeeInfo?.role === 'admin') && leave.employee_name && (
                              <p className="text-sm text-gray-600 mt-1 flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                {leave.employee_name}
                              </p>
                            )}
                          </div>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            leave.status === 'approved' ? 'bg-green-100 text-green-800' :
                            leave.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {leave.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div><span className="text-gray-600">From: </span><span className="font-medium">{new Date(leave.from_date).toLocaleDateString()}</span></div>
                          <div><span className="text-gray-600">To: </span><span className="font-medium">{new Date(leave.to_date).toLocaleDateString()}</span></div>
                          <div><span className="text-gray-600">Applied: </span><span>{new Date(leave.created_at).toLocaleDateString()}</span></div>
                          {leave.approved_rejected_at && (
                            <div><span className="text-gray-600">{leave.status === 'approved' ? 'Approved' : 'Rejected'}: </span><span>{new Date(leave.approved_rejected_at).toLocaleDateString()}</span></div>
                          )}
                        </div>
                        <div className="mt-3 text-sm">
                          <span className="text-gray-600">Reason: </span>
                          <p className="mt-1 text-gray-800">{leave.reason}</p>
                        </div>
                        {leave.admin_comment && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                            <span className="text-gray-600 font-medium">Admin Comment: </span>
                            <p className="mt-1 text-gray-800">{leave.admin_comment}</p>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {showApplyModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowApplyModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Apply for Leave</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
                    <select
                      value={leaveForm.leave_type}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, leave_type: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                    >
                      <option value="Full Day">Full Day</option>
                      <option value="Half Day">Half Day</option>
                      <option value="Short Leave">Short Leave</option>
                      <option value="Sick Leave">Sick Leave</option>
                      <option value="Casual Leave">Casual Leave</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                    <input
                      type="date"
                      value={leaveForm.from_date}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, from_date: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                    <input
                      type="date"
                      value={leaveForm.to_date}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, to_date: e.target.value }))}
                      onBlur={checkScheduledClasses}
                      min={leaveForm.from_date}
                      className="w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                    />
                  </div>

                  {/* Check for scheduled classes button - MANDATORY */}
                  {leaveForm.from_date && leaveForm.to_date && (
                    <div>
                      <label className="block text-sm font-medium text-red-700 mb-2">
                        * Must check for scheduled classes before submitting
                      </label>
                      <button
                        type="button"
                        onClick={checkScheduledClasses}
                        disabled={checkingClasses || classesChecked}
                        className={`w-full inline-flex justify-center items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${
                          classesChecked 
                            ? 'border-green-300 bg-green-50 text-green-700 cursor-not-allowed' 
                            : 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
                        } disabled:opacity-50`}
                      >
                        {checkingClasses ? 'Checking...' : classesChecked ? '✓ Classes Checked' : 'Check Scheduled Classes (Required)'}
                      </button>
                    </div>
                  )}

                  {/* Show scheduled classes if any */}
                  {scheduledClasses.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-yellow-800 mb-2">
                            You have {scheduledClasses.length} scheduled class{scheduledClasses.length > 1 ? 'es' : ''} during this period
                          </h4>
                          {employeeInfo?.employment_type?.toLowerCase().includes('visiting') && (
                            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded">
                              <p className="text-xs font-medium text-blue-900">
                                💰 Selected Hours: {calculateSelectedHours()} hours
                              </p>
                              <p className="text-xs text-blue-700 mt-1">
                                Select which classes will be affected by your leave. These hours will be deducted from your salary.
                              </p>
                            </div>
                          )}
                          <p className="text-xs text-yellow-700 mb-3">
                            {employeeInfo?.employment_type?.toLowerCase().includes('visiting') 
                              ? 'Select affected classes and provide substitute/remarks:'
                              : 'Please provide substitute/remarks for each class:'}
                          </p>
                          <div className="space-y-3">
                            {scheduledClasses.map(session => (
                              <div key={session.session_id} className="bg-white p-3 rounded border border-yellow-200">
                                <div className="flex items-start gap-2">
                                  {employeeInfo?.employment_type?.toLowerCase().includes('visiting') && (
                                    <input
                                      type="checkbox"
                                      checked={selectedClasses[session.session_id] || false}
                                      onChange={(e) => setSelectedClasses(prev => ({ 
                                        ...prev, 
                                        [session.session_id]: e.target.checked 
                                      }))}
                                      className="mt-1"
                                    />
                                  )}
                                  <div className="flex-1">
                                    <div className="text-xs font-medium text-gray-900 mb-1">
                                      {session.subject_name} - {session.batch_name}
                                      {employeeInfo?.employment_type?.toLowerCase().includes('visiting') && (
                                        <span className="ml-2 text-blue-600">({session.duration_hours}h)</span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-600 mb-2">
                                      {new Date(session.scheduled_date).toLocaleDateString()} • {session.time_slot} • {session.classroom_name}
                                    </div>
                                    {selectedClasses[session.session_id] && (
                                      <textarea
                                        value={classRemarks[session.session_id] || ''}
                                        onChange={(e) => setClassRemarks(prev => ({ ...prev, [session.session_id]: e.target.value }))}
                                        placeholder="Substitute teacher name or arrangements..."
                                        rows={2}
                                        className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                                        required
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {scheduledClasses.length === 0 && leaveForm.from_date && leaveForm.to_date && !checkingClasses && classesChecked && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                      ✓ No scheduled classes during this period
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                    <textarea
                      value={leaveForm.reason}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, reason: e.target.value }))}
                      rows={4}
                      className="w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                      placeholder="Please provide a reason for your leave..."
                    />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleApplyLeave}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Submit Request
                </button>
                <button
                  onClick={() => setShowApplyModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyLeaves;
